// @greenlight-spec: 42-app-marketplace.md
// @greenlight-spec: decisions/0021-app-access-default-open-imperative-bindings.md
import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { api } from '@/lib/api';
import { mutationErrorMessage } from '@/lib/apiErrors';

/**
 * An app's reach for everyone not named on it: open to the organization, or only
 * the people named on the app. There is no third "listed" state — access is what
 * decides whether the catalog shows a row.
 */
export type AppVisibility = 'marketplace' | 'restricted';

const PATCH: Record<AppVisibility, { access_restricted: boolean }> = {
  marketplace: { access_restricted: false },
  restricted: { access_restricted: true },
};

const TOAST: Record<AppVisibility, string> = {
  marketplace: 'Anyone in your organization can find and open this app.',
  restricted: 'Only the people listed can find or open this app.',
};

export function toAppVisibility(app: { accessRestricted: boolean }): AppVisibility {
  return app.accessRestricted ? 'restricted' : 'marketplace';
}

export function useSetAppVisibility(
  appId: string,
  app: { accessRestricted: boolean },
): {
  value: AppVisibility;
  set: (next: AppVisibility) => Promise<void>;
  isPending: boolean;
} {
  const queryClient = useQueryClient();
  const mutation = api.apps.update.useMutation();
  const server = toAppVisibility(app);
  const [value, setValue] = React.useState<AppVisibility>(server);

  React.useEffect(() => {
    setValue(server);
  }, [server]);

  const set = async (next: AppVisibility): Promise<void> => {
    const previous = value;
    setValue(next);
    try {
      const res = await mutation.mutateAsync({ params: { id: appId }, body: PATCH[next] });
      if (res.status >= 300) throw res;
      toast.success(TOAST[next]);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['apps.get', appId] }),
        queryClient.invalidateQueries({ queryKey: ['apps'] }),
        queryClient.invalidateQueries({ queryKey: ['marketplace'] }),
      ]);
    } catch (err) {
      setValue(previous);
      toast.error(`Couldn't update access: ${mutationErrorMessage(err)}`);
    }
  };

  return { value, set, isPending: mutation.isPending };
}
